import { useState } from "react";
import { Link } from "react-router-dom";
import { Grid } from "@mui/material";
import { PlusMinusButton } from "../../../components/PlusMinusButton";
import { Row } from "../../../components/Row";
import {
  DateInput,
  NumberInput,
  Selection,
  StringInput,
} from "../../../components/Inputs";
import { Check } from "../../../components/Check/Check";
import {
  RequestHandlerReturn,
  useRequestHandler,
} from "../../../hooks/requestHandler";
import {
  createTournamentEvent,
  importTournamentStats,
  savePage,
} from "../../../state/actions/tournamentActions";
import * as types from "../../../types/tournamentTypes";

interface EditProps {
  mode: "CREATE" | "UPDATE";
  page: Partial<types.TournamentPage>;
}

export function EditPageForm(props: EditProps) {
  const requestHandler = useRequestHandler({});

  const [pageData, setPageData] = useState<Partial<types.TournamentPage>>(
    props.page
  );
  const getAlternateIds = () => {
    let result = "";
    props.page.alternateIds?.forEach((id, index) => {
      result += `${index > 0 ? "," : ""}${id}`;
    });
    return result;
  };
  const [alternateIds, setAlternateIds] = useState(getAlternateIds());

  if (!pageData.pageType) {
    return (
      <Selection
        caption="Page Type:"
        values={types.TPageTypeDescriptions}
        value={pageData.pageType}
        onChange={(value) =>
          setPageData({
            ...pageData,
            pageType: value,
          } as types.TournamentPage)
        }
        placeholder="Select a Page Type"
      />
    );
  }
  return (
    <div className="main-box">
      <div className="main-box">
        {props.mode === "CREATE" ? (
          <>
            <p style={{ marginTop: 0, fontSize: 18, fontWeight: "bold" }}>
              {types.TPageTypeDescriptions[pageData.pageType]}
            </p>
            <StringInput
              caption="Page ID:"
              value={pageData.pageId}
              onChange={(value) => setPageData({ ...pageData, pageId: value })}
              placeholder="Page ID"
            />
            {pageData.pageType === "TOURNAMENT" && (
              <StringInput
                caption="Secondary ID:"
                value={pageData.secondaryId}
                onChange={(value) =>
                  setPageData({ ...pageData, secondaryId: value })
                }
                placeholder="Page Secondary ID"
              />
            )}
          </>
        ) : (
          <p style={{ fontWeight: "bold", fontSize: 18 }}>
            {`${pageData.pageId}${
              pageData.secondaryId ? ` ${pageData.secondaryId}` : ""
            } - ${types.TPageTypeDescriptions[pageData.pageType]}`}
          </p>
        )}
        <StringInput
          caption="Alternate IDs:"
          value={alternateIds}
          onChange={setAlternateIds}
          belowText="Other IDs that might refer to this page. Deliminate IDs with commas."
        />
      </div>
      <div style={{ marginTop: 10 }} className="main-box">
        <div>
          <label className="field-label" htmlFor="secondary-id">
            Description
          </label>
        </div>
        <textarea
          value={pageData.description || ""}
          id="secondary-id"
          onChange={(e) =>
            setPageData({ ...pageData, description: e.target.value })
          }
        />
        <p style={{ fontSize: 12 }}>
          {pageData.pageType === "OTHER" || pageData.pageType === "SERIES"
            ? "For Misc and Series pages, description is required."
            : "A description will be auto-generated if you don't supply your own."}
        </p>
      </div>
      {(pageData.pageType === "MINISTRY" ||
        pageData.pageType === "TOURNAMENT") && (
        <div className="main-box" style={{ marginTop: 10 }}>
          {pageData.pageType === "TOURNAMENT" && (
            <>
              <Selection
                caption="Tier:"
                value={pageData.tier}
                placeholder="Select a Tier"
                values={types.TTierDescriptions}
                onChange={(value) => setPageData({ ...pageData, tier: value })}
              />
              <Check
                checked={pageData.isIndividual}
                style={{ width: 150 }}
                onClick={(checked) =>
                  setPageData({ ...pageData, isIndividual: checked })
                }
              >
                Is Individual
              </Check>
              <StringInput
                caption="Host Ministry:"
                value={pageData.host}
                onChange={(value) => setPageData({ ...pageData, host: value })}
              />
              <DateInput
                caption="Start Date:"
                value={pageData.startDate}
                onChange={(value) =>
                  setPageData({
                    ...pageData,
                    startDate: value,
                    endDate: pageData.endDate || value,
                  })
                }
              />
              <DateInput
                caption="End Date:"
                value={pageData.endDate}
                onChange={(value) =>
                  setPageData({ ...pageData, endDate: value })
                }
              />
              <NumberInput
                caption="Total Teams:"
                value={pageData.teamCount || 0}
                onChange={(value) =>
                  setPageData({ ...pageData, teamCount: value })
                }
              />
            </>
          )}
          {pageData.pageType === "MINISTRY" && (
            <>
              <StringInput
                caption="Ministry Name:"
                value={pageData.ministryName}
                onChange={(value) =>
                  setPageData({ ...pageData, ministryName: value })
                }
              />
              <Row style={{ marginTop: 10 }}>
                <button
                  className="link"
                  onClick={() =>
                    setPageData({
                      ...pageData,
                      currentTeams: {
                        ...pageData.currentTeams,
                        local: undefined,
                      },
                    })
                  }
                >
                  Remove Local Teams
                </button>
                <button
                  className="link"
                  style={{ marginLeft: 10 }}
                  onClick={() =>
                    setPageData({
                      ...pageData,
                      currentTeams: {
                        ...pageData.currentTeams,
                        postseason: undefined,
                      },
                    })
                  }
                >
                  Remove Postseason Teams
                </button>
              </Row>
            </>
          )}
          <StringInput
            caption="Location:"
            value={pageData.location}
            onChange={(value) => setPageData({ ...pageData, location: value })}
          />
        </div>
      )}
      {(pageData.pageType === "QUIZZER" ||
        pageData.pageType === "TEAM" ||
        pageData.pageType === "MINISTRY") && (
        <div className="main-box" style={{ marginTop: 10 }}>
          {(pageData.pageType === "QUIZZER" ||
            pageData.pageType === "TEAM") && (
            <>
              <StringInput
                caption="Ministry:"
                value={pageData.ministry}
                onChange={(value) =>
                  setPageData({ ...pageData, ministry: value })
                }
                style={{ margin: 0 }}
              />
              <button
                className="link"
                style={{ marginTop: 10 }}
                onClick={() =>
                  setPageData({ ...pageData, currentTournament: undefined })
                }
              >
                Remove Currently Quizzing
              </button>
            </>
          )}

          <NumberInput
            caption="First Active:"
            value={pageData.firstActive}
            onChange={(value) =>
              setPageData({
                ...pageData,
                firstActive: value,
              })
            }
          />
          <NumberInput
            caption="Last Active:"
            value={pageData.lastActive}
            onChange={(value) =>
              setPageData({
                ...pageData,
                lastActive: value,
              })
            }
            belowText="The years this quizzer/team was active for quizzing. Leave blank if N/A."
          />
          <StringInput
            caption="Not Active"
            value={pageData.notActive}
            onChange={(value) => setPageData({ ...pageData, notActive: value })}
            belowText="
            A description of when this page was not active. Does not need to
            conform to any format, and doesn't need to be a number"
          />
        </div>
      )}
      {pageData.pageType !== "OTHER" && pageData.pageType !== "REDIRECT" && (
        <>
          <div style={{ marginTop: 10 }} className="main-box">
            <h4 style={{ marginBottom: 4, marginTop: 0 }}>Additional Data</h4>
            <p style={{ marginTop: 0, fontSize: 14 }}>
              Provide any number of key-value pairs. Some keys have special
              meaning; see <Link to="/tournaments/help">Help</Link> for details.
            </p>
            {Object.keys(pageData.additionalData || {}).map((key, index) => (
              <Row key={index}>
                <input
                  value={key}
                  onChange={(e) => {
                    const newAdditional = {
                      ...pageData.additionalData,
                    };
                    const oldValue = newAdditional[key];
                    delete newAdditional[key];
                    newAdditional[e.target.value] = oldValue || "";
                    setPageData({ ...pageData, additionalData: newAdditional });
                  }}
                />
                <textarea
                  style={{ marginLeft: 5, width: 400 }}
                  value={pageData.additionalData?.[key]}
                  onChange={(e) => {
                    const newAdditional = {
                      ...pageData.additionalData,
                    };
                    newAdditional[key] = e.target.value;
                    setPageData({ ...pageData, additionalData: newAdditional });
                  }}
                />
                <PlusMinusButton
                  style={{ marginLeft: 5 }}
                  onClick={() => {
                    const newAdditional = {
                      ...pageData.additionalData,
                    };
                    delete newAdditional[key];
                    setPageData({ ...pageData, additionalData: newAdditional });
                  }}
                >
                  -
                </PlusMinusButton>
              </Row>
            ))}
            <PlusMinusButton
              style={{ marginTop: 5 }}
              onClick={() =>
                setPageData({
                  ...pageData,
                  additionalData: { ...pageData.additionalData, "": "" },
                })
              }
            >
              +
            </PlusMinusButton>
          </div>
          <div style={{ marginTop: 10 }} className="main-box">
            <h4 style={{ marginBottom: 4, marginTop: 0 }}>Trivia</h4>
            <p style={{ marginTop: 0, fontSize: 12 }}>
              Any miscellaneous information that might be related to the page.
            </p>
            {pageData.trivia?.map((trivia, index) => (
              <Row key={index} style={{ marginBottom: 2 }}>
                <input
                  value={trivia}
                  style={{ width: 500 }}
                  onChange={(e) => {
                    const newTrivia = [...pageData.trivia!];
                    newTrivia[index] = e.target.value;
                    setPageData({ ...pageData, trivia: newTrivia });
                  }}
                />
                <PlusMinusButton
                  style={{ marginLeft: 5 }}
                  onClick={() => {
                    const newTrivia = [...pageData.trivia!];
                    newTrivia.splice(index, 1);
                    setPageData({ ...pageData, trivia: newTrivia });
                  }}
                >
                  -
                </PlusMinusButton>
              </Row>
            ))}
            <PlusMinusButton
              style={{ marginTop: 5 }}
              onClick={() =>
                setPageData({
                  ...pageData,
                  trivia: [...(pageData.trivia || []), ""],
                })
              }
            >
              +
            </PlusMinusButton>
          </div>
        </>
      )}
      {pageData.pageType === "TOURNAMENT" && (
        <>
          <EditRosters page={pageData} onChange={setPageData} />
          <EditStandings page={pageData} onChange={setPageData} />
        </>
      )}
      <div style={{ marginTop: 20 }}>
        <button
          className="clickable"
          onClick={() => {
            const resultPage = {
              ...pageData,
              alternateIds: alternateIds.split(","),
            };
            requestHandler.runRequest(
              savePage(props.mode, resultPage),
              "Saving the page...",
              "Page saved!"
            );
          }}
        >
          Save Page
        </button>
      </div>
      {pageData.pageType === "TOURNAMENT" && props.mode === "UPDATE" && (
        <>
          <TournamentImportStats page={pageData} handler={requestHandler} />
          <TournamentAddEvent page={pageData} handler={requestHandler} />
        </>
      )}
      {requestHandler.snackbar}
    </div>
  );
}

// Tournament-Specific Data
interface EditRostersProps {
  page: Partial<types.Tournament>;
  onChange: (page: Partial<types.Tournament>) => void;
}
function EditRosters(props: EditRostersProps) {
  const [selectedTeamId, setSelectedTeamId] = useState<string>("");

  const thisTeam = selectedTeamId
    ? props.page.rosters![selectedTeamId]
    : undefined;
  const changeTeam = (
    team: types.TRoster,
    changedQuizzerName?: { old: string; new: string }
  ) => {
    const oldTeam = thisTeam!;
    const newPage = { ...props.page, rosters: { ...props.page.rosters } };
    // Remove old, add new
    delete newPage.rosters[oldTeam.teamName];
    newPage.rosters[team.teamName] = team;

    // Change of team name in stats
    if (team.teamName !== oldTeam.teamName && newPage.stats) {
      setSelectedTeamId(team.teamName);
      newPage.stats = {
        ...newPage.stats,
      };
      if (newPage.stats.team) {
        newPage.stats.team = [...newPage.stats.team];
        for (let i = 0; i < newPage.stats.team.length; i++) {
          const stat = newPage.stats.team[i];
          if (stat.name === oldTeam.teamName) {
            newPage.stats.team[i] = {
              ...stat,
              name: team.teamName,
            };
          }
        }
      }
      if (newPage.stats.quizzer) {
        newPage.stats.quizzer = [...newPage.stats.quizzer];
        for (let i = 0; i < newPage.stats.quizzer.length; i++) {
          const stat = newPage.stats.quizzer[i];
          if (stat.team === oldTeam.teamName) {
            newPage.stats.quizzer[i] = {
              ...stat,
              team: team.teamName,
            };
          }
        }
      }
    }
    // Change of quizzer name in stats
    if (changedQuizzerName && newPage.stats) {
      newPage.stats = {
        ...newPage.stats,
      };
      if (newPage.stats.quizzer) {
        newPage.stats.quizzer = [...newPage.stats.quizzer];
        for (let i = 0; i < newPage.stats.quizzer.length; i++) {
          const stat = newPage.stats.quizzer[i];
          if (stat.name === changedQuizzerName.old) {
            newPage.stats.quizzer[i] = {
              ...stat,
              name: changedQuizzerName.new,
            };
          }
        }
      }
    }
    props.onChange(newPage);
  };

  return (
    <div className="main-box" style={{ marginTop: 10 }}>
      <h4 style={{ marginBottom: 4, marginTop: 0 }}>Edit Rosters</h4>
      <button
        className="back-button"
        onClick={() => {
          props.onChange({
            ...props.page,
            rosters: {
              ...props.page.rosters,
              "[No Team Name]": {
                teamName: "[No Team Name]",
                quizzers: {},
              },
            },
          });
        }}
      >
        Add Team
      </button>
      <Grid container spacing={2} style={{ marginTop: 10 }}>
        <Grid>
          {Object.keys(props.page.rosters || {})
            .sort()
            .map((key, index) => (
              <div key={index}>
                <button className="link" onClick={() => setSelectedTeamId(key)}>
                  {props.page.rosters![key].teamName}
                </button>
              </div>
            ))}
        </Grid>
        {thisTeam && (
          <Grid>
            <StringInput
              caption="Team Name:"
              value={thisTeam.teamName}
              onChange={(value) => {
                const newTeamName = value || "[No Team Name]";
                changeTeam({ ...thisTeam, teamName: newTeamName });
                setSelectedTeamId(newTeamName);
              }}
              style={{ marginTop: 0 }}
            />
            <StringInput
              caption="Team ID:"
              value={thisTeam.teamId}
              onChange={(value) =>
                changeTeam({ ...thisTeam, teamId: value || undefined })
              }
              belowText="If different from team name"
            />
            <StringInput
              caption="Ministry:"
              value={thisTeam.ministry}
              onChange={(value) =>
                changeTeam({ ...thisTeam, ministry: value || undefined })
              }
            />
            <StringInput
              caption="Coach:"
              value={thisTeam.coach}
              onChange={(value) =>
                changeTeam({ ...thisTeam, coach: value || undefined })
              }
              style={{ marginBottom: 20 }}
            />
            <div>
              <Check
                checked={thisTeam.resultsExclude}
                onClick={(value) =>
                  changeTeam({ ...thisTeam, resultsExclude: value })
                }
              >
                Exclude from Results
              </Check>
              <button
                className="back-button"
                style={{ marginLeft: 10 }}
                onClick={() => {
                  const newTeams = {
                    ...props.page.rosters,
                  };
                  delete newTeams[selectedTeamId];
                  props.onChange({ ...props.page, rosters: newTeams });
                }}
              >
                Delete Team
              </button>
            </div>
            <h5>Quizzers</h5>
            <button
              className="back-button"
              onClick={() => {
                let minId = 0;
                Object.values(props.page.rosters!).forEach((roster) => {
                  Object.keys(roster.quizzers).forEach((quizzer) => {
                    const idNum = Number.parseInt(quizzer);
                    if (!isNaN(idNum) && idNum > minId) minId = idNum;
                  });
                });
                props.onChange({
                  ...props.page,
                  rosters: {
                    ...props.page.rosters,
                    [thisTeam.teamName]: {
                      ...thisTeam,
                      quizzers: {
                        ...thisTeam.quizzers,
                        [String(minId + 1)]: "",
                      },
                    },
                  },
                });
              }}
            >
              Add Quizzer
            </button>
            <p>
              Edit quizzers if you wish. This will not affect scores in both
              stats and quizzes.
            </p>
            {Object.keys(thisTeam.quizzers).map((quizzerId, index) => (
              <Row key={quizzerId}>
                <StringInput
                  caption={`Quizzer ${index + 1}`}
                  value={thisTeam.quizzers[quizzerId]}
                  onChange={(value) =>
                    changeTeam(
                      {
                        ...thisTeam,
                        quizzers: { ...thisTeam.quizzers, [quizzerId]: value },
                      },
                      { old: thisTeam.quizzers[quizzerId], new: value }
                    )
                  }
                />
                <Check
                  checked={thisTeam.dnf && thisTeam.dnf.includes(quizzerId)}
                  onClick={(checked) => {
                    const newDnf = new Set(thisTeam.dnf || []);
                    if (checked) newDnf.add(quizzerId);
                    else newDnf.delete(quizzerId);
                    const newTeam: types.TRoster = {
                      ...thisTeam,
                      dnf: newDnf.size === 0 ? undefined : Array.from(newDnf),
                    };
                    changeTeam(newTeam);
                  }}
                  style={{ width: 70, marginLeft: 5 }}
                >
                  DNF
                </Check>
                <Check
                  checked={
                    thisTeam.achievementsExclude &&
                    thisTeam.achievementsExclude.includes(quizzerId)
                  }
                  onClick={(checked) => {
                    const newAchievementsExclude = new Set(
                      thisTeam.achievementsExclude || []
                    );
                    if (checked) newAchievementsExclude.add(quizzerId);
                    else newAchievementsExclude.delete(quizzerId);
                    const newTeam: types.TRoster = {
                      ...thisTeam,
                      achievementsExclude:
                        newAchievementsExclude.size === 0
                          ? undefined
                          : Array.from(newAchievementsExclude),
                    };
                    changeTeam(newTeam);
                  }}
                  style={{ width: 160, marginLeft: 5 }}
                >
                  No Achievements
                </Check>
              </Row>
            ))}
          </Grid>
        )}
      </Grid>
    </div>
  );
}

interface EditRostersProps {
  page: Partial<types.Tournament>;
  onChange: (page: Partial<types.Tournament>) => void;
}
function EditStandings(props: EditRostersProps) {
  const [view, setView] = useState<"TEAM" | "INDIVIDUAL" | "NONE">("NONE");

  const getEditContent = () => {
    if (view === "INDIVIDUAL") {
      return (
        <Grid container spacing={5}>
          {props.page.stats?.quizzer.map((quizzer, index) => {
            const setQuizzer = (quizzer: types.TQuizzerStats) => {
              const quizzers = [...props.page.stats!.quizzer];
              quizzers[index] = quizzer;
              props.onChange({
                ...props.page,
                stats: {
                  ...props.page.stats!,
                  quizzer: quizzers,
                },
              });
            };
            return (
              <Grid key={index}>
                <p>{`${quizzer.name} - ${quizzer.team}`}</p>
                <NumberInput
                  caption="Points"
                  value={quizzer.points as number}
                  onChange={(value) =>
                    setQuizzer({ ...quizzer, points: value })
                  }
                />
                <NumberInput
                  caption="Errors"
                  value={quizzer.errors as number}
                  onChange={(value) =>
                    setQuizzer({ ...quizzer, errors: value })
                  }
                />
                <NumberInput
                  caption="Quiz Count"
                  value={quizzer.qCount as number}
                  onChange={(value) =>
                    setQuizzer({ ...quizzer, qCount: value || undefined })
                  }
                />
                <NumberInput
                  caption="Prize ($)"
                  value={quizzer.prize as number}
                  onChange={(value) =>
                    setQuizzer({ ...quizzer, prize: value || undefined })
                  }
                />
              </Grid>
            );
          })}
        </Grid>
      );
    }
    if (view === "TEAM") {
      return (
        <Grid container spacing={5}>
          {props.page.stats?.team.map((team, index) => {
            const setTeam = (team: types.TStats) => {
              const teams = [...props.page.stats!.team];
              teams[index] = team;
              props.onChange({
                ...props.page,
                stats: {
                  ...props.page.stats!,
                  team: teams,
                },
              });
            };
            return (
              <Grid key={index}>
                <p>{team.name}</p>
                <NumberInput
                  caption="Points"
                  value={team.points as number}
                  onChange={(value) => setTeam({ ...team, points: value })}
                />
                <NumberInput
                  caption="Errors"
                  value={team.errors as number}
                  onChange={(value) => setTeam({ ...team, errors: value })}
                />
                <NumberInput
                  caption="Quiz Count"
                  value={team.qCount as number}
                  onChange={(value) =>
                    setTeam({ ...team, qCount: value || undefined })
                  }
                />
                <NumberInput
                  caption="Prize ($)"
                  value={team.prize as number}
                  onChange={(value) =>
                    setTeam({ ...team, prize: value || undefined })
                  }
                />
              </Grid>
            );
          })}
        </Grid>
      );
    }

    return null;
  };

  return (
    <div>
      <h3>Edit Standings</h3>
      <Row>
        <Check
          checked={view === "NONE"}
          onClick={() => setView("NONE")}
          style={{ width: 150 }}
        >
          None
        </Check>
        <Check
          checked={view === "INDIVIDUAL"}
          onClick={() => setView("INDIVIDUAL")}
          style={{ width: 150 }}
        >
          Individual
        </Check>
        <Check
          checked={view === "TEAM"}
          onClick={() => setView("TEAM")}
          style={{ width: 150 }}
        >
          Team
        </Check>
      </Row>
      {getEditContent()}
    </div>
  );
}

// Tournament-Specific Data
interface TournamentImportStatsProps {
  page: Partial<types.Tournament>;
  handler: RequestHandlerReturn;
}
function TournamentImportStats(props: TournamentImportStatsProps) {
  const [teamStats, setTeamStats] = useState<string[]>([]);
  const [quizzerStats, setQuizzerStats] = useState<string[]>([]);

  const handleFileData = (e: ProgressEvent<FileReader>) => {
    const raw = e?.target?.result;

    if (!raw) {
      props.handler.showMessage("No data in file.", "error");
      return null;
    }
    const stringRaw = String(raw);
    const includesR = stringRaw.includes("\r\n");
    const data = String(raw).split(includesR ? "\r\n" : "\n");
    return data;
  };

  return (
    <div className="main-box" style={{ marginTop: 30 }}>
      <h4 style={{ margin: 0 }}>Import Stats</h4>
      <p>
        Upload files for stats. This will replace all stats and rosters for this
        tournament. Files must be tab-delimited (\t) with no header row. You
        only have to upload team or quizzer, not both.
      </p>
      <Row>
        <span>Team:</span>
        <input
          type="file"
          style={{ marginLeft: 5 }}
          onChange={(e) => {
            const reader = new FileReader();
            reader.readAsText(e.target.files![0]);
            reader.addEventListener("load", (e) => {
              const result = handleFileData(e);
              if (result) setTeamStats(result);
            });
          }}
        />
      </Row>
      <p>Columns: Rank (ignored), Team Name, Quiz Count, Points, Errors</p>
      <Row>
        <span>Quizzer:</span>
        <input
          type="file"
          style={{ marginLeft: 5 }}
          onChange={(e) => {
            const reader = new FileReader();
            reader.readAsText(e.target.files![0]);
            reader.addEventListener("load", (e) => {
              const result = handleFileData(e);
              if (result) setQuizzerStats(result);
            });
          }}
        />
      </Row>
      <p>
        Columns: Rank (ignored), First Name (ignored), Last Name (ignored), Full
        Name, Team, Points, Errors, Quiz Count
      </p>
      <p>
        Ministry must be included in team name. For example, "Riven - SBQ" or
        "Revelation - MWTFC". If ministries are included, they must be present
        for the team file but are optional for the quizzer file.
      </p>
      <button
        className="clickable"
        style={{ marginTop: 10 }}
        onClick={() => {
          if (!props.page.pageId) {
            props.handler.showMessage("Include a Page ID.", "error");
            return;
          }
          if (teamStats.length === 0 && quizzerStats.length === 0) {
            props.handler.showMessage("Import a file for stats.", "error");
            return;
          }
          props.handler.runRequest(
            importTournamentStats(
              props.page.pageId,
              props.page.secondaryId,
              teamStats,
              quizzerStats
            ),
            "Saving the page...",
            "Stats Imported!"
          );
        }}
      >
        Submit Stats
      </button>
    </div>
  );
}

function TournamentAddEvent(props: TournamentImportStatsProps) {
  const [eventName, setEventName] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [order, setOrder] = useState<number>(0);

  return (
    <div className="main-box" style={{ marginTop: 30 }}>
      <h4 style={{ margin: 0 }}>Add Event</h4>
      <StringInput
        caption="Event Name"
        value={eventName}
        onChange={setEventName}
      />
      <DateInput caption="Date" value={date} onChange={setDate} />
      <NumberInput caption="Order" value={order} onChange={setOrder} />
      <button
        className="clickable"
        style={{ marginTop: 10 }}
        onClick={() => {
          if (!props.page.pageId) {
            props.handler.showMessage("Include a Page ID.", "error");
            return;
          }
          props.handler.runRequest(
            createTournamentEvent(
              props.page.pageId,
              props.page.secondaryId || "",
              {
                eventName,
                date,
                order,
              }
            ),
            "Saving the event...",
            "Event Saved!"
          );
        }}
      >
        Save Event
      </button>
    </div>
  );
}
